import { IoIosArrowRoundForward } from 'react-icons/io';
import { IoTriangle } from 'react-icons/io5';
import { TbTriangleInvertedFilled } from 'react-icons/tb';
import { useState } from 'react';
import { WRAPPED_SOL, ZERO_ADDRESS } from '@shared/utils/config/networks';
import { FaBookmark, FaRegBookmark } from 'react-icons/fa';
import { toast } from 'sonner';
import { CATEGORIES, EVENTS, LogToMixpanel } from '@/lib/misc/mixpanel';
import { abbreviateNumber, timeStampDifferenceToDays, toSpecialFixed } from '@/lib/misc/utils';
import { getNetworkLogo, fallbackLogo } from '@/lib/misc/tokenImageHelper';
import { useSession } from '@/contexts/LitSessionProvider';
import { useSavedToken } from '@/hooks/saved/useSavedToken';
import DTTopTokenModal from './desktop/normie-mode/DTTopTokenModal';
import { haveDetails } from '@/lib/asset/nativeAddressHelper';
import TokenImage from '../shared/TokenImage';
export default function TokenCard({
  tokenItem,
  timeFrame,
  searchResult,
  handleTokenClick,
  onTokenClick,
  mode,
  setIsAssetModalOpen,
  setAssetModalData
}) {
  const {
    PKP
  } = useSession();
  const [isTopTokenModalOpen, setIsTopTokenOpenModal] = useState(false);
  const {
    checkIsSaved,
    handleSaveSingle,
    handleDeleteTokenFromHome
  } = useSavedToken({});
  const isMultiToken = haveDetails(tokenItem);
  const isEthereum = tokenItem?.symbol === 'ETH' && mode === 'native';
  const isSolana = tokenItem?.address == WRAPPED_SOL;
  const change = searchResult?.[`change${timeFrame}`] ? parseFloat(searchResult?.[`change${timeFrame}`]) : searchResult?.tokenInfo?.[`change${timeFrame}`] ? searchResult?.tokenInfo?.[`change${timeFrame}`] : 0;
  return <div key={`${tokenItem?.name}`} className={'h-[70px] flex px-4 w-full justify-between items-center hover:cursor-pointer md:hover:bg-genius-blue relative'} onClick={() => {
    if (isEthereum) {
      setIsAssetModalOpen(true);
      setAssetModalData({
        price: searchResult?.priceUSD,
        change
      });
    } else if (isMultiToken) {
      setIsTopTokenOpenModal(true);
    } else {
      handleTokenClick(tokenItem, searchResult);
    }
  }} data-sentry-component="TokenCard" data-sentry-source-file="TokenCard.tsx">
      {mode === 'saved' && <div onClick={e => {
      e.stopPropagation();
      if (!PKP) {
        toast.error('Create an account to save tokens to your watchlist.', {
          duration: 5000
        });
        return;
      }
      if (!checkIsSaved(tokenItem?.address, tokenItem?.networkId)) {
        LogToMixpanel({
          pkp: PKP,
          eventName: EVENTS.asset.saveToken,
          category: CATEGORIES.asset,
          metaData: {
            address: tokenItem?.address
          }
        });
        handleSaveSingle({
          tokenToSave: tokenItem
        });
      } else {
        handleDeleteTokenFromHome(tokenItem);
      }
    }} className='flex items-center mr-4'>
          {checkIsSaved(tokenItem?.address, tokenItem?.networkId) ? <FaBookmark className='w-[12px] h-[12px] text-genius-cream cursor-pointer' /> : <FaRegBookmark className='w-[12px] h-[12px] text-genius-cream cursor-pointer' />}
        </div>}

      {/* Token image, symbol, name & mcap */}
      <div className='flex items-center gap-3 relative w-full' key={`${tokenItem?.networkId}`}>
        <TokenImage mainImageSrc={(tokenItem?.address == ZERO_ADDRESS || isSolana ? getNetworkLogo(Array.isArray(tokenItem.networkId) ? tokenItem.networkId[tokenItem.networkId.length - 1] : tokenItem.networkId) : tokenItem?.imageSmallUrl || tokenItem?.info?.imageThumbUrl || tokenItem?.imageLargeUrl || fallbackLogo)?.replace('large', 'small')} badgeImageSrc={mode === 'native' ? null : isMultiToken ? '/static/tokenlogos/gen.png' : getNetworkLogo(tokenItem?.networkId)} size={32} badgeSize={16} data-sentry-element="TokenImage" data-sentry-source-file="TokenCard.tsx" />
        <div className={'flex flex-col gap-y-1 justify-start text-left'}>
          <p className='text-base/4 text-genius-cream'>
            {tokenItem?.name?.length > 23 ? tokenItem?.name?.slice(0, 23) + '...' : tokenItem?.name}
          </p>

          <div className={'text-xs/3 text-genius-cream/70 font-light flex items-center'}>
            {mode == 'native' ? <>{tokenItem?.name} </> : <> {tokenItem?.symbol}</>}
            {searchResult?.marketCap && mode !== 'native' ? ' | $' + abbreviateNumber(searchResult?.marketCap) : ''}
            {(mode === 'trending' || mode === 'gainers' || mode === 'new') && <>
                {isSolana ? ' | Native ' : ''}
                {searchResult?.createdAt && ' | ' + timeStampDifferenceToDays(parseInt(searchResult?.createdAt))}
              </>}

            {isEthereum || isMultiToken ? <div className='flex items-center text-[12px] text-genius-cream/50 ml-2'>
                Details{' '}
                <IoIosArrowRoundForward className='w-[16px] h-[16px] translate-y-[0.75px]' />
              </div> : <></>}
          </div>
        </div>
      </div>

      {/* Token price & change */}
      <div className={`flex flex-col text-right gap-y-2 max-w-18 text-wrap`}>
        <p className='text-base/4 text-genius-cream text-right text-wrap'>
          $
          {searchResult?.priceUSD ? toSpecialFixed(searchResult?.priceUSD, 2, true).value : searchResult?.prices ? toSpecialFixed(searchResult?.prices[0].priceUsd, 2, true).value : ''}
        </p>
        <div className='flex gap-1 items-center justify-end text-right'>
          {change >= 0 ? <div className='flex items-center text-right'>
              <IoTriangle color='#34D399' size={12} />

              <p className='text-xs text-gray-400 ml-1 text-right'>{`${toSpecialFixed(change * 100, 2)?.value}%`}</p>
            </div> : <div className='flex items-center text-right'>
              <TbTriangleInvertedFilled color='#FF5B5B' size={12} />

              <p className='text-xs text-red-400 ml-1 text-right'>{`${toSpecialFixed(Math.abs(change * 100), 2).value}%`}</p>
            </div>}
        </div>
      </div>

      {!isEthereum && isMultiToken ? <DTTopTokenModal onTokenClick={onTokenClick} isModalOpen={isTopTokenModalOpen} toggleModal={() => setIsTopTokenOpenModal(!isTopTokenModalOpen)} token={searchResult} change={change} /> : null}
    </div>;
}